// src/observability/errorReporter.ts
// Self-contained client error reporter. Duplicated per frontend repo
// (see ems-api docs/observability/phase-3-frontend-reporter.md).
// Posts events to the backend ingest endpoint; never throws; no-op until init'd with enabled=true.
import { moduleFromRoute } from './moduleFromRoute';

export interface ObservabilityContext {
    userId?: string | null;
    organizationId?: string | null;
}

export interface ObservabilityConfig {
    ingestUrl: string;
    ingestKey: string;
    project: string;
    release: string;
    environment: string;
    enabled: boolean;
    getContext?: () => ObservabilityContext;
}

interface Breadcrumb {
    timestamp: string;
    category: string;
    message?: string;
    data?: Record<string, unknown>;
}

const MAX_BREADCRUMBS = 20;
const DEDUPE_WINDOW_MS = 10_000;
const STACK_LIMIT = 12_000;

let config: ObservabilityConfig | null = null;
const breadcrumbs: Breadcrumb[] = [];
const recent = new Map<string, number>();
let pageHiding = false;

function toError(value: unknown): Error {
    if (value instanceof Error) {
        return value;
    }
    if (typeof value === 'string') {
        return new Error(value);
    }
    try {
        return new Error(JSON.stringify(value));
    } catch {
        return new Error('Unknown error');
    }
}

function nowIso(): string {
    return new Date().toISOString();
}

function isDuplicate(key: string): boolean {
    const now = Date.now();
    const last = recent.get(key);
    if (last !== undefined && now - last < DEDUPE_WINDOW_MS) {
        return true;
    }
    recent.set(key, now);
    if (recent.size > 200) {
        for (const [entryKey, entryTime] of recent) {
            if (now - entryTime > DEDUPE_WINDOW_MS) {
                recent.delete(entryKey);
            }
        }
    }
    return false;
}

export function addBreadcrumb(category: string, message?: string, data?: Record<string, unknown>): void {
    breadcrumbs.push({ timestamp: nowIso(), category, message, data });
    if (breadcrumbs.length > MAX_BREADCRUMBS) {
        breadcrumbs.splice(0, breadcrumbs.length - MAX_BREADCRUMBS);
    }
}

function send(payload: Record<string, unknown>): void {
    if (!config || !config.enabled) {
        return;
    }
    try {
        void fetch(config.ingestUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'x-ingest-key': config.ingestKey },
            body: JSON.stringify(payload),
            keepalive: true,
            credentials: 'omit',
        }).catch(() => undefined);
    } catch {
        // Reporting must never break the app.
    }
}

export function captureException(
    error: unknown,
    extra?: Record<string, unknown>,
    level: 'warning' | 'error' | 'fatal' = 'error',
): void {
    if (!config || !config.enabled) {
        return;
    }
    try {
        const err = toError(error);
        const route = typeof window !== 'undefined' ? window.location.pathname : '';
        const key = `${err.name}:${err.message}:${route}`;
        if (isDuplicate(key)) {
            return;
        }
        const ctx = config.getContext ? config.getContext() : {};
        send({
            project: config.project,
            source: 'frontend',
            release: config.release,
            environment: config.environment,
            level,
            module: moduleFromRoute(route),
            type: err.name || 'Error',
            message: err.message,
            stack: err.stack ? err.stack.slice(0, STACK_LIMIT) : undefined,
            breadcrumbs: breadcrumbs.slice(-MAX_BREADCRUMBS),
            clientContext: {
                route,
                url: typeof window !== 'undefined' ? window.location.href : '',
                userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
                userId: ctx.userId ?? null,
                organizationId: ctx.organizationId ?? null,
                ...(extra ?? {}),
            },
            occurredAt: nowIso(),
        });
    } catch {
        // swallow — never throw from the reporter
    }
}

interface AxiosLikeError {
    response?: { status?: number };
    config?: { method?: string; url?: string };
    code?: string;
    message?: string;
    name?: string;
    __CANCEL__?: boolean;
}

function isCanceledApiError(error: AxiosLikeError): boolean {
    return error.code === 'ERR_CANCELED' ||
        error.name === 'CanceledError' ||
        error.__CANCEL__ === true ||
        error.message === 'canceled';
}

/** A transport failure with no HTTP response that happens while the tab is
 *  backgrounded/unloading or the client is offline is a browser-initiated
 *  abort (e.g. Safari cancels in-flight XHRs on tab blur), not a server fault. */
function isBrowserAbortedTransport(): boolean {
    if (pageHiding) {
        return true;
    }
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return true;
    }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return true;
    }
    return false;
}

/** Client 4xx that signal a frontend/contract bug (not user error) and should surface. */
const REPORTABLE_CLIENT_STATUSES = new Set([400, 422]);

/** Which HTTP statuses warrant an error-tracking event: 5xx server faults
 *  (excluding maintenance 503) and client-contract 4xx (400/422). Expected 4xx
 *  (401/403/404/429) and 503 are intentionally ignored as noise. */
function isReportableHttpStatus(status: number): boolean {
    if (status === 503) {
        return false;
    }
    if (status >= 500) {
        return true;
    }
    return REPORTABLE_CLIENT_STATUSES.has(status);
}

/** Report failed API calls. Server errors (>=500, excl. maintenance 503),
 *  client-contract 4xx (400/422, as warnings), and real transport failures;
 *  expected 4xx and browser-aborted/canceled requests are skipped. */
export function captureApiError(error: unknown): void {
    if (!config || !config.enabled) {
        return;
    }
    const e = error as AxiosLikeError;
    if (isCanceledApiError(e)) {
        return;
    }
    const status = e.response?.status;
    if (typeof status === 'number' && !isReportableHttpStatus(status)) {
        return;
    }
    // No HTTP response while hidden/unloading/offline → browser abort, not a backend fault.
    if (typeof status !== 'number' && isBrowserAbortedTransport()) {
        return;
    }
    const method = (e.config?.method ?? 'GET').toUpperCase();
    const url = e.config?.url ?? '';
    const type = typeof status === 'number' ? `HTTP_${status}` : (e.code ?? 'NetworkError');
    const synthetic = new Error(`API ${method} ${url} failed: ${status ?? e.message ?? 'network error'}`);
    synthetic.name = type;
    // A client-caused 4xx (400/422) is a frontend/contract bug → warning;
    // 5xx and transport failures are hard faults → error.
    const level = typeof status === 'number' && status < 500 ? 'warning' : 'error';
    captureException(
        synthetic,
        { kind: 'api', apiStatus: status ?? null, apiUrl: url, apiMethod: method },
        level,
    );
}

function installNavigationBreadcrumbs(): void {
    if (typeof window === 'undefined' || typeof window.history === 'undefined') {
        return;
    }
    const record = (): void => addBreadcrumb('navigation', window.location.pathname);
    window.addEventListener('popstate', record);
    const originalPushState = window.history.pushState.bind(window.history);
    window.history.pushState = (data: unknown, unused: string, url?: string | URL | null): void => {
        originalPushState(data, unused, url ?? undefined);
        record();
    };
}

export function initErrorReporter(cfg: ObservabilityConfig): void {
    config = cfg;
    if (!cfg.enabled || typeof window === 'undefined') {
        return;
    }
    window.addEventListener('error', (event: ErrorEvent) => {
        captureException((event.error ?? event.message) as unknown, { kind: 'window.onerror' });
    });
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
        captureException(event.reason as unknown, { kind: 'unhandledrejection' });
    });
    window.addEventListener('pagehide', () => {
        pageHiding = true;
    });
    window.addEventListener('pageshow', () => {
        pageHiding = false;
    });
    installNavigationBreadcrumbs();
}
